import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import leadService from '../../../api/crm/leads/leadService';
import { getLeadStatusColor } from '../../../utils/constants';
import { useLeads } from '../../../context/LeadContext';
import {
  Users,
  Plus,
  Download,
  Eye,
  Edit,
  Trash2,
  X,
  UserPlus,
  MessageCircle,
  CheckCircle,
  Award,
  XCircle,
  UserCheck,
  UserX,
  ClipboardList,
  Target,
  Filter,
  Briefcase,
  Trophy,
  Ban,
  Activity,
  Globe,
  Facebook,
  Instagram,
  Linkedin,
  Phone,
  Mail,
  Building,
  Footprints,
  PenTool,
  Share2,
  MessageSquare,
  Search,
  AlertCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  PhoneOff,
  Video,
  VideoOff,
  DollarSign,
  CreditCard,
  CheckSquare,
  Flame,
  Thermometer,
  Snowflake,
  Layers,
  ShoppingBag,
  PhoneCall,
  Heart
} from 'lucide-react';
import {
  sanitizeSearchInput,
  handleSearchInputChange,
  getCharacterCount,
  FIELD_LIMITS
} from '../../../utils/inputValidation';
import {
  FacebookIcon,
  InstagramIcon,
  LinkedInIcon,
  GoogleIcon,
  WhatsAppIcon
} from './LeadSourceIcons';
import Button from '../../../components/common/Button/Button';
import { useAuth } from '../../../context/AuthContext';
import teamManagementService from '../../../api/crm/team-management/teamManagementService';
import { toast, Toaster } from 'react-hot-toast';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatIndianAmount, formatIndianNumber } from '../../../utils/amountFormatter';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import { get } from '../../../api/apiClient';
import { API_ENDPOINTS } from '../../../api/config';

const LeadDashboard = ({ onViewLead, onAddLead }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();

  const handleViewLead = (leadId) => {
    if (onViewLead) {
      onViewLead(leadId);
    } else {
      navigate(`/lead_detail_360/${leadId}`);
    }
  };

  const handleAddLead = () => {
    if (onAddLead) {
      onAddLead();
    } else {
      navigate('/manual_lead_entry');
    }
  };

  const handleFilterRedirect = (paramName, value) => {
    navigate(`/lead_list_filters?${paramName}=${encodeURIComponent(value)}`);
  };

  // Search input ref
  const searchInputRef = useRef(null);
  const isInitialMount = useRef(true);

  // Lead context for pagination
  const { leads, loading: leadsLoading, error: leadsError, loadLeads, pagination, clearError, deleteLead, leadStatuses, mapStageToStatusName } = useLeads();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Lead List Filters state (matching LeadListFilters.jsx)
  const [searchTerm, setSearchTerm] = useState('');

  // Sorting state
  const [sortColumn, setSortColumn] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');

  // State for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();

  // Filtering state
  const [selectedManager, setSelectedManager] = useState(searchParams.get('manager') || '');
  const [selectedSalesExecutive, setSelectedSalesExecutive] = useState(searchParams.get('salesExecutive') || '');
  const [managers, setManagers] = useState([]);
  const [salesExecutives, setSalesExecutives] = useState([]);

  // Searchable dropdown state
  const [managerSearchOpen, setManagerSearchOpen] = useState(false);
  const [managerSearchTerm, setManagerSearchTerm] = useState('');
  const [debouncedManagerSearch, setDebouncedManagerSearch] = useState('');
  const [selectedManagerData, setSelectedManagerData] = useState(null);

  const [executiveSearchOpen, setExecutiveSearchOpen] = useState(false);
  const [executiveSearchTerm, setExecutiveSearchTerm] = useState('');
  const [debouncedExecutiveSearch, setDebouncedExecutiveSearch] = useState('');
  const [selectedExecutiveData, setSelectedExecutiveData] = useState(null);

  const [selectedPeriod, setSelectedPeriod] = useState(searchParams.get('period') || 'all_time');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || ''
  });

  const [leadStats, setLeadStats] = useState([
    // Lead Pipeline Cards (Row 1)
    { title: 'Total Leads', value: '0', icon: Users, color: 'bg-blue-500' },
    { title: 'New Leads', value: '0', icon: UserPlus, color: 'bg-emerald-500' },
    { title: 'Contacted', value: '0', icon: MessageCircle, color: 'bg-amber-500' },
    { title: 'Qualified', value: '0', icon: CheckCircle, color: 'bg-purple-500' },
    { title: 'Converted', value: '0', icon: Award, color: 'bg-green-600' },
    { title: 'Disqualified', value: '0', icon: XCircle, color: 'bg-red-500' },
    // Assignment Cards (Row 2)
    { title: 'Assigned Leads', value: '0', icon: UserCheck, color: 'bg-cyan-500' },
    { title: 'Unassigned Leads', value: '0', icon: UserX, color: 'bg-orange-500' },
    { title: 'Assignment Rules', value: '0', icon: ClipboardList, color: 'bg-indigo-500' },
    // Rules Cards (Row 3)
    { title: 'Scoring Rules', value: '0', icon: Target, color: 'bg-rose-500' },
    { title: 'Qualification Rules', value: '0', icon: Filter, color: 'bg-violet-500' },
    { title: 'Open Opportunities', value: '0', icon: Briefcase, color: 'bg-sky-500' },
    // Opportunity & Activity Cards (Row 4)
    { title: 'Won', value: '0', icon: Trophy, color: 'bg-emerald-600' },
    { title: 'Lost', value: '0', icon: Ban, color: 'bg-slate-500' },
    { title: 'Total Activities', value: '0', icon: Activity, color: 'bg-fuchsia-500' }
  ]);
  // Initialize source data as empty - will be populated from database
  const [sourceData, setSourceData] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    totalLeads: 0,
    newLeads: 0,
    contactedLeads: 0,
    qualifiedLeads: 0,
    convertedLeads: 0,
    disqualifiedLeads: 0,
    assignedLeads: 0,
    unassigned: 0,
    assignmentRulesCount: 0,
    scoringRulesCount: 0,
    qualificationCriteriaCount: 0,
    openOpportunities: 0,
    closedWonOpportunities: 0,
    closedLostOpportunities: 0
  });


  // Debounce search terms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedManagerSearch(managerSearchTerm), 300);
    return () => clearTimeout(timer);
  }, [managerSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedExecutiveSearch(executiveSearchTerm), 300);
    return () => clearTimeout(timer);
  }, [executiveSearchTerm]);

  // Synchronize state variables to URL search parameters to persist on refresh
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      
      if (selectedManager) params.set('manager', selectedManager);
      else params.delete('manager');
      
      if (selectedSalesExecutive) params.set('salesExecutive', selectedSalesExecutive);
      else params.delete('salesExecutive');
      
      if (selectedPeriod) params.set('period', selectedPeriod);
      else params.delete('period');
      
      if (customDateRange.startDate) params.set('startDate', customDateRange.startDate);
      else params.delete('startDate');
      
      if (customDateRange.endDate) params.set('endDate', customDateRange.endDate);
      else params.delete('endDate');
      
      return params;
    }, { replace: true });
  }, [selectedManager, selectedSalesExecutive, selectedPeriod, customDateRange, setSearchParams]);

  // Synchronize display labels mapping when lists load dynamically
  useEffect(() => {
    if (selectedManager && managers.length > 0) {
      const found = managers.find(m => (m.id || m._id) === selectedManager);
      if (found) setSelectedManagerData(found);
    }
  }, [selectedManager, managers]);

  useEffect(() => {
    if (selectedSalesExecutive && salesExecutives.length > 0) {
      const found = salesExecutives.find(e => (e.id || e._id) === selectedSalesExecutive);
      if (found) setSelectedExecutiveData(found);
    }
  }, [selectedSalesExecutive, salesExecutives]);

  // Query for Managers
  const { data: managersData } = useQuery({
    queryKey: ['crmManagers', debouncedManagerSearch],
    queryFn: () => teamManagementService.getCRMUsers({
      roleFilter: 'sales',
      search: debouncedManagerSearch
    }),
    enabled: currentUser?.role === 'super_admin'
  });

  useEffect(() => {
    if (managersData?.success) {
      const salesManagers = managersData.users.filter(u => u.role === 'sales_manager' || u.role === 'export_manager');
      setManagers(salesManagers);
    }
  }, [managersData]);

  // Auth: id = User._id (Lead.assignedTo), _id = Employee._id (team-management API)
  const currentUserId = currentUser?.id || currentUser?.userId;
  const currentEmployeeId = currentUser?._id;

  // Query for Sales Executives
  const managerIdForExecutives = currentUser?.role === 'super_admin'
    ? selectedManager
    : (currentEmployeeId || currentUserId || '');

  const { data: executivesData } = useQuery({
    queryKey: ['salesExecutives', managerIdForExecutives, debouncedExecutiveSearch],
    queryFn: () => teamManagementService.getCRMUsers({
      managerId: managerIdForExecutives,
      search: debouncedExecutiveSearch
    }),
    enabled: !!managerIdForExecutives || currentUser?.role === 'sales_manager' || currentUser?.role === 'export_manager'
  });

  useEffect(() => {
    if (executivesData?.success) {
      const executives = executivesData.users.filter(u => ['sales_executive', 'export_executive', 'business_relationship_manager', 'business_development_executive'].includes(u.role));
      setSalesExecutives(executives);
    } else if (executivesData) {
      setSalesExecutives([]);
    }
  }, [executivesData]);

  // Dashboard Filters
  const dashboardFilters = React.useMemo(() => {
    const f = {};
    // Lead.assignedTo uses User._id — same as LeadListFilters assignee filter
    const executiveUserId = selectedExecutiveData?.user || (selectedSalesExecutive && selectedExecutiveData ? selectedExecutiveData.user : selectedSalesExecutive) || null;

    if (selectedManagerData?.user) {
      f.managerId = selectedManagerData.user;
    } else if (selectedManager) {
      const matchingManager = managers.find(m => (m.id || m._id) === selectedManager);
      if (matchingManager?.user) f.managerId = matchingManager.user;
    }

    if (executiveUserId) {
      f.salesExecutiveId = executiveUserId;
      f.assignedTo = executiveUserId;
    }

    if (currentUser?.role === 'sales_manager' || currentUser?.role === 'export_manager') {
      if (currentUserId) f.managerId = currentUserId;
    } else if (['sales_executive', 'export_executive', 'business_relationship_manager', 'business_development_executive'].includes(currentUser?.role)) {
      if (currentUserId) {
        f.salesExecutiveId = currentUserId;
        f.assignedTo = currentUserId;
      }
    }

    // Date filtering
    if (customDateRange.startDate || customDateRange.endDate) {
      if (customDateRange.startDate) f.startDate = customDateRange.startDate;
      if (customDateRange.endDate) f.endDate = customDateRange.endDate;
      f.range = 'custom';
    } else {
      f.range = selectedPeriod;
    }

    return f;
  }, [selectedManagerData, selectedExecutiveData, selectedManager, selectedSalesExecutive, managers, salesExecutives, currentUser, currentUserId, selectedPeriod, customDateRange]);

  useEffect(() => {
    setCurrentPage(1);
  }, [dashboardFilters]);

  // Query for Statistics
  const { data: statsData, isLoading: isLoadingStats } = useQuery({
    queryKey: ['leadStatistics', dashboardFilters],
    queryFn: () => leadService.getLeadStatistics(dashboardFilters),
    enabled: !!currentUser
  });

  const sourceFilteredDashboardFilters = useMemo(() => {
    if (selectedSources.length === 0) return null;
    return {
      ...dashboardFilters,
      sources: selectedSources
    };
  }, [dashboardFilters, selectedSources]);

  const { data: filteredStatsData, isLoading: isLoadingFilteredStats } = useQuery({
    queryKey: ['leadStatistics', 'sourceFiltered', sourceFilteredDashboardFilters],
    queryFn: () => leadService.getLeadStatistics(sourceFilteredDashboardFilters),
    enabled: !!currentUser && selectedSources.length > 0
  });

  const sectionStats = useMemo(() => {
    if (selectedSources.length > 0) {
      return filteredStatsData?.statistics || null;
    }
    return statsData?.statistics || null;
  }, [selectedSources, filteredStatsData, statsData]);

  const isSectionStatsLoading = selectedSources.length > 0
    ? isLoadingFilteredStats && !filteredStatsData
    : isLoadingStats && !statsData;

  const toggleSourceFilter = (sourceName) => {
    setSelectedSources((prev) => {
      const normalizedName = sourceName.toLowerCase();
      const isSelected = prev.some((source) => source.toLowerCase() === normalizedName);
      if (isSelected) {
        return prev.filter((source) => source.toLowerCase() !== normalizedName);
      }
      return [...prev, sourceName];
    });
  };

  const isSourceSelected = (sourceName) => {
    const normalizedName = sourceName.toLowerCase();
    return selectedSources.some((source) => source.toLowerCase() === normalizedName);
  };

  // Query for active lead sources
  const { data: activeSourcesResponse, isLoading: isLoadingActiveSources } = useQuery({
    queryKey: ['activeLeadSources'],
    queryFn: () => get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_SOURCES}?active=true`),
    enabled: !!currentUser
  });

  const activeSourcesList = useMemo(() => {
    return activeSourcesResponse?.data || [];
  }, [activeSourcesResponse]);

  // Query for Video Statuses
  const { data: videoStatusesResponse } = useQuery({
    queryKey: ['activeVideoStatuses'],
    queryFn: () => get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_VIDEO_STATUSES}?active=true`),
    enabled: !!currentUser
  });
  const videoStatusesList = useMemo(() => videoStatusesResponse?.data || [], [videoStatusesResponse]);

  // Query for Order Statuses
  const { data: orderStatusesResponse } = useQuery({
    queryKey: ['activeOrderStatuses'],
    queryFn: () => get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_ORDER_STATUSES}?active=true`),
    enabled: !!currentUser
  });
  const orderStatusesList = useMemo(() => orderStatusesResponse?.data || [], [orderStatusesResponse]);

  // Query for Call Statuses
  const { data: callStatusesResponse } = useQuery({
    queryKey: ['activeCallStatuses'],
    queryFn: () => get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_CALL_STATUSES}?active=true`),
    enabled: !!currentUser
  });
  const callStatusesList = useMemo(() => callStatusesResponse?.data || [], [callStatusesResponse]);

  // Query for Interest Statuses
  const { data: interestStatusesResponse } = useQuery({
    queryKey: ['activeInterestStatuses'],
    queryFn: () => get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_INTERESTS}?active=true`),
    enabled: !!currentUser
  });
  const interestStatusesList = useMemo(() => interestStatusesResponse?.data || [], [interestStatusesResponse]);

  const leadStatusDisplayList = useMemo(() => {
    const systemStatusConfig = {
      new: { icon: UserPlus, color: 'bg-emerald-500', cardBg: 'bg-emerald-50', cardHover: 'hover:bg-emerald-100', cardBorder: 'border-emerald-100', countColor: 'text-emerald-700', countBg: 'bg-emerald-100/80' },
      contacted: { icon: MessageCircle, color: 'bg-amber-500', cardBg: 'bg-amber-50', cardHover: 'hover:bg-amber-100', cardBorder: 'border-amber-100', countColor: 'text-amber-700', countBg: 'bg-amber-100/80' },
      qualified: { icon: CheckCircle, color: 'bg-purple-500', cardBg: 'bg-purple-50', cardHover: 'hover:bg-purple-100', cardBorder: 'border-purple-100', countColor: 'text-purple-700', countBg: 'bg-purple-100/80' },
      converted: { icon: Award, color: 'bg-green-600', cardBg: 'bg-green-50', cardHover: 'hover:bg-green-100', cardBorder: 'border-green-100', countColor: 'text-green-700', countBg: 'bg-green-100/80' },
      disqualified: { icon: XCircle, color: 'bg-red-500', cardBg: 'bg-red-50', cardHover: 'hover:bg-red-100', cardBorder: 'border-red-100', countColor: 'text-red-700', countBg: 'bg-red-100/80' }
    };

    const defaultCardStyle = {
      icon: ClipboardList,
      color: 'bg-indigo-500',
      cardBg: 'bg-indigo-50',
      cardHover: 'hover:bg-indigo-100',
      cardBorder: 'border-indigo-100',
      countColor: 'text-indigo-700',
      countBg: 'bg-indigo-100/80'
    };

    if (Array.isArray(leadStatuses) && leadStatuses.length > 0) {
      return leadStatuses.map((status) => {
        const valLower = (status.value || '').toLowerCase().trim();
        const config = systemStatusConfig[valLower] || defaultCardStyle;
        return {
          value: status.value,
          name: status.name || status.label || status.value,
          valLower,
          ...config
        };
      });
    }

    return Object.entries(systemStatusConfig).map(([key, config]) => ({
      value: key,
      name: key.charAt(0).toUpperCase() + key.slice(1),
      valLower: key,
      ...config
    }));
  }, [leadStatuses]);

  // Query for Company Sources
  const { data: companySourcesResponse } = useQuery({
    queryKey: ['activeCompanySources'],
    queryFn: () => get(`${API_ENDPOINTS.CRM.CONFIG.COMPANY_SOURCES}`),
    enabled: !!currentUser
  });
  const companySourcesList = useMemo(() => companySourcesResponse?.data || [], [companySourcesResponse]);

  // Load leads using useLeads context for pagination (matching LeadListFilters)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentUser) {
        const apiFilters = {
          ...dashboardFilters,
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm || undefined
        };

        // Remove undefined values
        Object.keys(apiFilters).forEach(key =>
          apiFilters[key] === undefined && delete apiFilters[key]
        );

        // Loading leads with filters
        loadLeads(apiFilters);
      }
    }, isInitialMount.current ? 0 : 500); // Load immediately on mount, debounce on subsequent changes

    // Mark that initial mount is complete after setting up the first load
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }

    return () => clearTimeout(timeoutId);
  }, [currentUser, dashboardFilters, currentPage, itemsPerPage, searchTerm, loadLeads]);

  // Update stats state when query data changes
  useEffect(() => {
    if (statsData?.success && statsData.statistics) {
      const stats = statsData.statistics;

      const totalLeadsCount = typeof stats.totalLeads === 'number' ? stats.totalLeads : parseInt(stats.totalLeads) || 0;
      const newLeadsCount = typeof stats.newLeads === 'number' ? stats.newLeads : parseInt(stats.newLeads) || 0;
      const contactedCount = typeof stats.contactedLeads === 'number' ? stats.contactedLeads : parseInt(stats.contactedLeads) || 0;
      const qualifiedCount = typeof stats.qualifiedLeads === 'number' ? stats.qualifiedLeads : parseInt(stats.qualifiedLeads) || 0;
      const convertedCount = typeof stats.convertedLeads === 'number' ? stats.convertedLeads : parseInt(stats.convertedLeads) || 0;
      const disqualifiedCount = typeof stats.disqualifiedLeads === 'number' ? stats.disqualifiedLeads : parseInt(stats.disqualifiedLeads) || 0;
      const assignedCount = typeof stats.assignedLeads === 'number' ? stats.assignedLeads : parseInt(stats.assignedLeads) || 0;
      const unassignedCount = typeof stats.unassigned === 'number' ? stats.unassigned : parseInt(stats.unassigned) || 0;
      const assignmentRulesCount = typeof stats.assignmentRulesCount === 'number' ? stats.assignmentRulesCount : parseInt(stats.assignmentRulesCount) || 0;
      const scoringRulesCount = typeof stats.scoringRulesCount === 'number' ? stats.scoringRulesCount : parseInt(stats.scoringRulesCount) || 0;
      const qualificationRulesCount = typeof stats.qualificationCriteriaCount === 'number' ? stats.qualificationCriteriaCount : parseInt(stats.qualificationCriteriaCount) || 0;
      const openOppsCount = typeof stats.openOpportunities === 'number' ? stats.openOpportunities : parseInt(stats.openOpportunities) || 0;
      const closedWonCount = typeof stats.closedWonOpportunities === 'number' ? stats.closedWonOpportunities : parseInt(stats.closedWonOpportunities) || 0;
      const closedLostCount = typeof stats.closedLostOpportunities === 'number' ? stats.closedLostOpportunities : parseInt(stats.closedLostOpportunities) || 0;
      const totalActivitiesCount = typeof stats.totalActivities === 'number' ? stats.totalActivities : parseInt(stats.totalActivities) || 0;

      const cards = [];

      // 1. Total Leads (Global Metric)
      cards.push({
        title: 'Total Leads',
        value: totalLeadsCount.toString(),
        icon: Users,
        color: 'bg-blue-500',
        stageValue: 'all'
      });

      // 2. Dynamic Lead Statuses from LeadContext config
      const systemStatusConfig = {
        'new': { title: 'New Leads', icon: UserPlus, color: 'bg-emerald-500' },
        'contacted': { title: 'Contacted', icon: MessageCircle, color: 'bg-amber-500' },
        'qualified': { title: 'Qualified', icon: CheckCircle, color: 'bg-purple-500' },
        'converted': { title: 'Converted', icon: Award, color: 'bg-green-600' },
        'disqualified': { title: 'Disqualified', icon: XCircle, color: 'bg-red-500' }
      };

      if (Array.isArray(leadStatuses) && leadStatuses.length > 0) {
        leadStatuses.forEach(status => {
          const valLower = (status.value || '').toLowerCase().trim();
          
          if (systemStatusConfig[valLower]) {
            cards.push({
              title: systemStatusConfig[valLower].title,
              value: (stats.stageCounts?.[valLower] ?? 0).toString(),
              icon: systemStatusConfig[valLower].icon,
              color: systemStatusConfig[valLower].color,
              stageValue: status.value
            });
          } else {
            // Custom dynamically added status!
            cards.push({
              title: status.name || status.label || status.value,
              value: (stats.stageCounts?.[valLower] ?? 0).toString(),
              icon: ClipboardList, // default fallback icon
              color: 'bg-indigo-500', // default fallback color
              stageValue: status.value
            });
          }
        });
      } else {
        // Fallback to defaults if leadStatuses context isn't loaded yet
        Object.keys(systemStatusConfig).forEach(key => {
          const stageValue = key === 'new' ? 'new' : key === 'contacted' ? 'contacted' : key === 'qualified' ? 'qualified' : key === 'converted' ? 'converted' : 'disqualified';
          const statValue = key === 'new' ? newLeadsCount : key === 'contacted' ? contactedCount : key === 'qualified' ? qualifiedCount : key === 'converted' ? convertedCount : disqualifiedCount;
          cards.push({
            title: systemStatusConfig[key].title,
            value: statValue.toString(),
            icon: systemStatusConfig[key].icon,
            color: systemStatusConfig[key].color,
            stageValue: stageValue
          });
        });
      }

      // 3. Other Non-status KPI cards
      cards.push(
        { title: 'Assigned Leads', value: assignedCount.toString(), icon: UserCheck, color: 'bg-cyan-500' },
        { title: 'Unassigned Leads', value: unassignedCount.toString(), icon: UserX, color: 'bg-orange-500' },
        { title: 'Assignment Rules', value: assignmentRulesCount.toString(), icon: ClipboardList, color: 'bg-indigo-500' },
        { title: 'Scoring Rules', value: scoringRulesCount.toString(), icon: Target, color: 'bg-rose-500' },
        { title: 'Qualification Rules', value: qualificationRulesCount.toString(), icon: Filter, color: 'bg-violet-500' },
        { title: 'Open Opportunities', value: openOppsCount.toString(), icon: Briefcase, color: 'bg-sky-500' },
        { title: 'Total Activities', value: totalActivitiesCount.toString(), icon: Activity, color: 'bg-fuchsia-500' }
      );

      setLeadStats(cards);

      if (stats.sourceData && Array.isArray(stats.sourceData)) {
        setSourceData(stats.sourceData);
      } else {
        setSourceData([]);
      }

      setDashboardStats({
        totalLeads: totalLeadsCount,
        newLeads: newLeadsCount,
        contactedLeads: contactedCount,
        qualifiedLeads: qualifiedCount,
        convertedLeads: convertedCount,
        disqualifiedLeads: disqualifiedCount,
        assignedLeads: assignedCount,
        unassigned: unassignedCount,
        assignmentRulesCount: assignmentRulesCount,
        scoringRulesCount: scoringRulesCount,
        qualificationCriteriaCount: qualificationRulesCount,
        openOpportunities: openOppsCount,
        closedWonOpportunities: closedWonCount,
        closedLostOpportunities: closedLostCount,
        totalActivities: totalActivitiesCount
      });
    }
  }, [statsData, leadStatuses]);

  // Unified loading state
  const isLoading = isLoadingStats || leadsLoading;

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (managerSearchOpen && !event.target.closest('.manager-dropdown')) {
        setManagerSearchOpen(false);
        setManagerSearchTerm('');
      }
      if (executiveSearchOpen && !event.target.closest('.executive-dropdown')) {
        setExecutiveSearchOpen(false);
        setExecutiveSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [managerSearchOpen, executiveSearchOpen]);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Helper function to check if lead is assigned to current user and get display text
  const getOwnerDisplayText = (lead) => {
    const ownerName = lead.owner || 'Unassigned';
    if (ownerName === 'Unassigned') return ownerName;

    // Check if the lead is assigned to the current user
    const currentUserId = currentUser?.id || currentUser?._id;
    const assignedToId = lead.assignedTo?._id || lead.assignedTo;

    // Also check by comparing names if IDs don't match
    const currentUserFullName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim().toLowerCase();
    const ownerNameLower = ownerName.toLowerCase();

    const isAssignedToCurrentUser = (
      (currentUserId && assignedToId && currentUserId.toString() === assignedToId.toString()) ||
      (currentUserFullName && ownerNameLower && currentUserFullName === ownerNameLower)
    );

    return isAssignedToCurrentUser ? `${ownerName} (You)` : ownerName;
  };

  // Helper function to render owner name with highlighted "(You)" badge
  const renderOwnerWithHighlight = (lead) => {
    const ownerName = lead.owner || 'Unassigned';
    if (ownerName === 'Unassigned') return <span>{ownerName}</span>;

    // Check if the lead is assigned to the current user
    const currentUserId = currentUser?.id || currentUser?._id;
    const assignedToId = lead.assignedTo?._id || lead.assignedTo;

    // Also check by comparing names if IDs don't match
    const currentUserFullName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim().toLowerCase();
    const ownerNameLower = ownerName.toLowerCase();

    const isAssignedToCurrentUser = (
      (currentUserId && assignedToId && currentUserId.toString() === assignedToId.toString()) ||
      (currentUserFullName && ownerNameLower && currentUserFullName === ownerNameLower)
    );

    if (isAssignedToCurrentUser) {
      return (
        <>
          {ownerName} <span className="text-emerald-600 font-semibold">(You)</span>
        </>
      );
    }
    return <span>{ownerName}</span>;
  };

  // Helper function to get lead name (matching LeadListFilters)
  const getLeadName = (lead) => {
    if (lead.firstName && lead.lastName) {
      return `${lead.firstName} ${lead.lastName}`;
    } else if (lead.firstName) {
      return lead.firstName;
    } else if (lead.lastName) {
      return lead.lastName;
    } else if (lead.name) {
      return lead.name;
    } else {
      return 'Unknown Lead';
    }
  };

  // Show delete confirmation modal
  const handleDeleteClick = (leadId) => {
    setLeadToDelete(leadId);
    setShowDeleteModal(true);
  };

  // Close delete modal without deleting
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setLeadToDelete(null);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!leadToDelete) {
      setShowDeleteModal(false);
      return;
    }

    try {
      const result = await deleteLead(leadToDelete);
      if (result.success) {
        toast.success('Lead deleted successfully');
        // Refresh the leads list with current pagination
        await loadLeads({ page: currentPage, limit: itemsPerPage });
      } else {
        toast(result.error || 'Unable to delete lead. Please try again.', {
          icon: '⚠️',
          style: { background: '#f59e0b', color: '#fff' }
        });
      }
    } catch (error) {
      toast('Unable to delete lead. Please check your connection and try again.', {
        icon: '⚠️',
        style: { background: '#f59e0b', color: '#fff' }
      });
    } finally {
      setShowDeleteModal(false);
      setLeadToDelete(null);
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  };


  // Use leads directly from API - all filtering and sorting is done server-side
  // This matches LeadListFilters exactly
  const filteredLeads = leads || [];


  const getScoreColor = (score) => {
    switch (score) {
      case 'Hot': return 'bg-red-100 text-red-800';
      case 'Warm': return 'bg-yellow-100 text-yellow-800';
      case 'Cold': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted leads for the table
  const getSortedLeads = () => {
    if (!sortColumn || !filteredLeads || filteredLeads.length === 0) return filteredLeads;

    return [...filteredLeads].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'name') {
        const aName = getLeadName(a).toLowerCase();
        const bName = getLeadName(b).toLowerCase();
        aValue = aName;
        bValue = bName;
      } else if (sortColumn === 'source') {
        aValue = (a.source || '').toLowerCase();
        bValue = (b.source || '').toLowerCase();
      } else if (sortColumn === 'score') {
        aValue = (a.score || '').toLowerCase();
        bValue = (b.score || '').toLowerCase();
      } else if (sortColumn === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      } else if (sortColumn === 'owner') {
        aValue = (a.owner || '').toLowerCase();
        bValue = (b.owner || '').toLowerCase();
      } else if (sortColumn === 'value') {
        aValue = Number(a.leadValue) || 0;
        bValue = Number(b.leadValue) || 0;
      } else if (sortColumn === 'updatedAt') {
        aValue = new Date(a.updatedAt || a.createdAt || 0).getTime();
        bValue = new Date(b.updatedAt || b.createdAt || 0).getTime();
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getStatusColor = (status) => {
    return getLeadStatusColor(status);
  };

  const handleViewAllLeads = () => {
    navigate('/lead_list_filters');
  };

  const handleExportLeads = () => {
    try {
      // Helper function to escape CSV values
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        // If value contains comma, quote, or newline, wrap in quotes and escape quotes
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      const pushSection = (csvLines, title, headers, rows) => {
        csvLines.push(title);
        csvLines.push(headers.join(','));
        if (rows.length === 0) {
          csvLines.push('No data available');
        } else {
          rows.forEach((row) => {
            csvLines.push(row.map(escapeCSV).join(','));
          });
        }
        csvLines.push('');
      };

      const stats = sectionStats || statsData?.statistics || {};

      // Build CSV content - matching exact dashboard format
      const csvLines = [];

      // Lead Pipeline / KPI cards (same cards shown at top of page)
      pushSection(
        csvLines,
        'Lead Pipeline Metrics',
        ['Metric', 'Value'],
        (leadStats || []).map((stat) => [stat.title, stat.value])
      );

      // Lead Sources — use active sources list + counts (same as UI)
      const sourceRows = (activeSourcesList || []).map((activeSource) => {
        const statsSource = (sourceData || []).find(
          (s) => s.name?.toLowerCase() === activeSource.name?.toLowerCase()
        );
        return [
          activeSource.name,
          statsSource?.count || 0,
          `${statsSource?.value ?? '0.0'}%`
        ];
      });
      pushSection(csvLines, 'Lead Sources Distribution', ['Source', 'Count', 'Percentage'], sourceRows);

      // Lead Status
      pushSection(
        csvLines,
        'Lead Status',
        ['Status', 'Count'],
        (leadStatusDisplayList || []).map((status) => [
          status.name,
          stats.stageCounts?.[status.valLower] || 0
        ])
      );

      // Lead Temperature
      pushSection(
        csvLines,
        'Lead Temperature',
        ['Temperature', 'Count'],
        [
          ['Hot', stats.temperatureCounts?.hot || 0],
          ['Warm', stats.temperatureCounts?.warm || 0],
          ['Cold', stats.temperatureCounts?.cold || 0]
        ]
      );

      // Company Source
      pushSection(
        csvLines,
        'Company Source',
        ['Company Source', 'Count'],
        [
          ['Agrosaf', stats.companySourceCounts?.agrosaf || 0],
          ['Alosafe', stats.companySourceCounts?.alosafe || 0]
        ]
      );

      // Video Status
      pushSection(
        csvLines,
        'Video Status',
        ['Status', 'Count'],
        (videoStatusesList || []).map((status) => [
          status.name,
          stats.videoStatusCounts?.[status._id] || 0
        ])
      );

      // Order Status
      pushSection(
        csvLines,
        'Order Status',
        ['Status', 'Count'],
        (orderStatusesList || []).map((status) => [
          status.name,
          stats.orderStatusCounts?.[status._id] || 0
        ])
      );

      // Call Status
      pushSection(
        csvLines,
        'Call Status',
        ['Status', 'Count'],
        (callStatusesList || []).map((status) => [
          status.name,
          stats.callStatusCounts?.[status._id] || 0
        ])
      );

      // Interest Status
      pushSection(
        csvLines,
        'Interest Status',
        ['Status', 'Count'],
        (interestStatusesList || []).map((status) => [
          status.name,
          stats.interestCounts?.[status._id] || 0
        ])
      );

      // Leads Table Data - Use sorted leads to match table exactly
      const sortedLeads = getSortedLeads();
      csvLines.push('Recent Leads');
      csvLines.push('Name,Company,Email,Source,Score,Status,Owner,Value,Last Activity');
      if (sortedLeads && sortedLeads.length > 0) {
        sortedLeads.forEach(lead => {
          const name = escapeCSV(getLeadName(lead));
          const company = escapeCSV(lead.company || 'No company');
          const email = escapeCSV(lead.email || 'No email');
          const source = escapeCSV(lead.source || 'Manual Entry');
          const score = escapeCSV(lead.score || 'N/A');
          const status = escapeCSV(mapStageToStatusName(lead.stage) || 'New');
          const owner = escapeCSV(getOwnerDisplayText(lead));
          const value = escapeCSV(lead.leadValue ? `₹${formatIndianAmount(lead.leadValue)}` : '₹0');
          const lastActivity = escapeCSV(formatDate(lead.updatedAt || lead.createdAt));

          csvLines.push(`${name},${company},${email},${source},${score},${status},${owner},${value},${lastActivity}`);
        });
      } else {
        csvLines.push('No leads available');
      }

      // Create CSV content
      const csvContent = csvLines.join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lead_dashboard_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Dashboard data exported successfully');
    } catch (error) {
      toast('Unable to export dashboard data. Please check your connection and try again.', {
        icon: '⚠️',
        style: { background: '#f59e0b', color: '#fff' }
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex space-y-4 justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Lead Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Monitor lead volume and conversion rates.</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
          <div className="flex items-center space-x-3">
            {(!customDateRange.startDate && !customDateRange.endDate) && (
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="all_time">All Time</option>
                <option value="today">Today</option>
                <option value="last_7_days">Last 7 days</option>
                <option value="last_30_days">Last 30 days</option>
                <option value="last_60_days">Last 60 days</option>
                <option value="last_90_days">Last 90 days</option>
              </select>
            )}

            <DateRangeFilter
              startDate={customDateRange.startDate}
              endDate={customDateRange.endDate}
              onChange={(range) => setCustomDateRange(range)}
              allowFutureDates={false}
            />

            {(customDateRange.startDate || customDateRange.endDate) && (
              <Button
                variant="outline"
                onClick={() => {
                  setCustomDateRange({ startDate: '', endDate: '' });
                  setSelectedPeriod('all_time');
                }}
              >
                Clear
              </Button>
            )}
          </div>

          <PermissionGate permission="lead_management" action="read">
            <Button
              variant="outline"
              icon={<Download className="h-4 w-4" />}
              onClick={handleExportLeads}
              className="w-full sm:w-auto"
            >
              Export
            </Button>
          </PermissionGate>
          <PermissionGate permission="lead_management" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => onAddLead ? onAddLead() : navigate('/manual_lead_entry')}
              className="w-full sm:w-auto"
            >
              Create Lead
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {isLoadingStats && !statsData ? (
          [...Array(5)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 animate-pulse">
              <div className="flex items-center">
                <div className="h-12 w-12 bg-gray-200 rounded-lg mr-3"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            </div>
          ))
        ) : (
          leadStats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-lg shadow-sm border border-gray-200 px-3 py-4 overflow-hidden cursor-pointer hover:shadow-md transition-shadow hover:border-blue-300"
                onClick={() => {
                  let filterData = {};

                  if (stat.stageValue) {
                    if (stat.stageValue === 'all') {
                      filterData.status = 'all';
                      filterData.assignee = 'all';
                      filterData.owner = 'all';
                      filterData.source = 'all';
                      filterData.score = 'all';
                      filterData.region = 'all';
                    } else {
                      filterData.status = stat.stageValue;
                    }
                  } else {
                    switch (stat.title) {
                      case 'Open Opportunities':
                        filterData.status = 'converted';
                        break;
                      case 'Won':
                        filterData.status = 'closed_won';
                        break;
                      case 'Lost':
                        filterData.status = 'closed_lost';
                        break;
                      case 'Assigned Leads':
                        filterData.assignee = 'all_assigned';
                        filterData.owner = 'all_assigned';
                        break;
                      case 'Unassigned Leads':
                        filterData.assignee = 'Unassigned';
                        filterData.owner = 'Unassigned';
                        break;
                      case 'Assignment Rules':
                        navigate('/lead_assignment');
                        return;
                      case 'Scoring Rules':
                      case 'Qualification Rules':
                        navigate('/lead_scoring_qualification');
                        return;
                      case 'Total Activities':
                        navigate('/lead_activities');
                        return;
                      default:
                        break;
                    }
                  }

                  navigate('/lead_list_filters', { state: { filters: filterData } });
                }}
              >
                <div className="flex items-center">
                  <div className={`p-2 sm:p-3 rounded-lg ${stat.color} flex-shrink-0 mr-3`}>
                    <IconComponent className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 capitalize">{stat.title}</p>
                    <p className="text-lg sm:text-xl font-bold text-gray-900 mt-1 sm:mt-2">{formatIndianNumber(stat.value)}</p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>


      {/* Charts Section */}
      <div className="grid grid-cols-1 gap-6">
        {/* Source Mix Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-2 sm:space-y-0">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Lead Sources</h3>
              {selectedSources.length > 0 && (
                <p className="text-xs text-blue-600 mt-1">
                  Filtering dashboard sections by: {selectedSources.join(', ')}. Click a selected source again to remove it.
                </p>
              )}
            </div>
          </div>
          {/* Source icons mapping */}
          {(() => {
            const sourceIcons = {
              'Website': Globe,
              'Facebook': FacebookIcon,
              'Instagram': InstagramIcon,
              'LinkedIn': LinkedInIcon,
              'WhatsApp': WhatsAppIcon,
              'Manual Entry': PenTool
            };

            const brandColors = {
              'Website': '#2563EB',     // Blue 600
              'Facebook': '#1877F2',    // Brand Facebook
              'Instagram': 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)', // Brand Instagram Gradient
              'LinkedIn': '#0A66C2',    // Brand LinkedIn
              'WhatsApp': '#25D366',    // Brand WhatsApp
              'Manual Entry': '#6B7280' // Gray 500
            };

            if ((isLoadingStats || isLoadingActiveSources) && (!statsData || !activeSourcesResponse)) {
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-pulse">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="h-8 w-8 bg-gray-200 rounded-lg flex-shrink-0"></div>
                        <div className="flex-1 space-y-2 min-w-0">
                          <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                          <div className="h-2 bg-gray-200 rounded w-1/4"></div>
                        </div>
                      </div>
                      <div className="h-3 bg-gray-200 rounded w-8"></div>
                    </div>
                  ))}
                </div>
              );
            }

            return activeSourcesList.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No active lead sources found.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {activeSourcesList.map((activeSource, index) => {
                  const statsSource = sourceData.find(s => s.name.toLowerCase() === activeSource.name.toLowerCase());
                  const count = statsSource ? statsSource.count : 0;
                  const value = statsSource ? statsSource.value : '0.0';
                  const IconComponent = sourceIcons[activeSource.name] || Globe;
                  const brandColor = brandColors[activeSource.name] || '#6B7280';
                  const isSelected = isSourceSelected(activeSource.name);

                  return (
                    <div
                      key={index}
                      onClick={() => toggleSourceFilter(activeSource.name)}
                      className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                        isSelected
                          ? 'bg-blue-50 border-2 border-blue-400 shadow-sm'
                          : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100 hover:border-gray-200'
                      }`}
                    >
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div
                          className="p-2 rounded-lg flex-shrink-0"
                          style={{ background: brandColor }}
                        >
                          <IconComponent className="h-4 w-4 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="text-sm font-medium text-gray-900 block truncate capitalize" title={activeSource.name}>{activeSource.name}</span>
                          <span className="text-xs text-gray-500">{formatIndianNumber(count)} leads</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 flex-shrink-0">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full"
                            style={{
                              width: `${Math.max(parseFloat(value) || 0, 2)}%`,
                              background: brandColor
                            }}
                          ></div>
                        </div>
                        <span className="text-sm font-bold text-gray-900 w-12 text-right">{value}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Lead Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Lead Status</h3>
          </div>
          {isSectionStatsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 animate-pulse">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 h-20">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="h-10 w-10 bg-gray-200 rounded-xl flex-shrink-0"></div>
                    <div className="flex-1 space-y-2 min-w-0">
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-2 bg-gray-200 rounded w-1/3"></div>
                    </div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded w-8 flex-shrink-0"></div>
                </div>
              ))}
            </div>
          ) : leadStatusDisplayList.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No lead statuses configured.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {leadStatusDisplayList.map((status) => {
                const count = sectionStats?.stageCounts?.[status.valLower] || 0;
                const IconComponent = status.icon;
                return (
                  <div
                    key={status.value}
                    onClick={() => handleFilterRedirect('status', status.value)}
                    className={`flex items-center justify-between p-4 ${status.cardBg} ${status.cardHover} transition-all rounded-xl border ${status.cardBorder} cursor-pointer group shadow-sm hover:shadow`}
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className={`p-3 ${status.color} rounded-xl text-white group-hover:scale-110 transition-transform flex-shrink-0`}>
                        <IconComponent className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-semibold text-gray-900 block truncate capitalize" title={status.name}>{status.name}</span>
                        <span className="text-xs text-gray-500 truncate block">Pipeline stage</span>
                      </div>
                    </div>
                    <span className={`text-lg font-bold ${status.countColor} ${status.countBg} px-3 py-1 rounded-lg ml-2 flex-shrink-0`}>
                      {formatIndianNumber(count)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Lead Temperature */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Lead Temperature</h3>
          </div>
          {isSectionStatsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-pulse">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 h-20">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="h-10 w-10 bg-gray-200 rounded-xl flex-shrink-0"></div>
                    <div className="flex-1 space-y-2 min-w-0">
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-2 bg-gray-200 rounded w-1/3"></div>
                    </div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded w-8 flex-shrink-0"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Hot Card */}
              <div
                onClick={() => handleFilterRedirect('score', 'hot')}
                className="flex items-center justify-between p-4 bg-red-50 hover:bg-red-100 transition-all rounded-xl border border-red-100 cursor-pointer group shadow-sm hover:shadow"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-red-500 rounded-xl text-white group-hover:scale-110 transition-transform">
                    <Flame className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-gray-900 block">Hot</span>
                    <span className="text-xs text-gray-500">Urgent conversion focus</span>
                  </div>
                </div>
                <span className="text-lg font-bold text-red-700 bg-red-100/80 px-3 py-1 rounded-lg">
                  {formatIndianNumber(sectionStats?.temperatureCounts?.hot || 0)}
                </span>
              </div>

              {/* Warm Card */}
              <div
                onClick={() => handleFilterRedirect('score', 'warm')}
                className="flex items-center justify-between p-4 bg-amber-50 hover:bg-amber-100 transition-all rounded-xl border border-amber-100 cursor-pointer group shadow-sm hover:shadow"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-amber-500 rounded-xl text-white group-hover:scale-110 transition-transform">
                    <Thermometer className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-gray-900 block">Warm</span>
                    <span className="text-xs text-gray-500">Regular nurturing required</span>
                  </div>
                </div>
                <span className="text-lg font-bold text-amber-700 bg-amber-100/80 px-3 py-1 rounded-lg">
                  {formatIndianNumber(sectionStats?.temperatureCounts?.warm || 0)}
                </span>
              </div>

              {/* Cold Card */}
              <div
                onClick={() => handleFilterRedirect('score', 'cold')}
                className="flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 transition-all rounded-xl border border-blue-100 cursor-pointer group shadow-sm hover:shadow"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-blue-500 rounded-xl text-white group-hover:scale-110 transition-transform">
                    <Snowflake className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-gray-900 block">Cold</span>
                    <span className="text-xs text-gray-500">Low engagement/re-engage</span>
                  </div>
                </div>
                <span className="text-lg font-bold text-blue-700 bg-blue-100/80 px-3 py-1 rounded-lg">
                  {formatIndianNumber(sectionStats?.temperatureCounts?.cold || 0)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Company Source */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Company Source</h3>
          </div>
          {isSectionStatsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-pulse">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 h-20">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="h-10 w-10 bg-gray-200 rounded-xl flex-shrink-0"></div>
                    <div className="flex-1 space-y-2 min-w-0">
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-2 bg-gray-200 rounded w-1/3"></div>
                    </div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded w-8 flex-shrink-0"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Agrosaf Card */}
              <div
                onClick={() => handleFilterRedirect('companySource', 'agrosaf')}
                className="flex items-center justify-between p-4 bg-emerald-50 hover:bg-emerald-100 transition-all rounded-xl border border-emerald-100 cursor-pointer group shadow-sm hover:shadow"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-emerald-500 rounded-xl text-white group-hover:scale-110 transition-transform">
                    <Layers className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-gray-900 block">Agrosaf</span>
                    <span className="text-xs text-gray-500">Leads captured from domestic channels</span>
                  </div>
                </div>
                <span className="text-lg font-bold text-emerald-700 bg-emerald-100/80 px-3 py-1 rounded-lg">
                  {formatIndianNumber(sectionStats?.companySourceCounts?.agrosaf || 0)}
                </span>
              </div>

              {/* Alosafe Card */}
              <div
                onClick={() => handleFilterRedirect('companySource', 'alosafe')}
                className="flex items-center justify-between p-4 bg-sky-50 hover:bg-sky-100 transition-all rounded-xl border border-sky-100 cursor-pointer group shadow-sm hover:shadow"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-sky-500 rounded-xl text-white group-hover:scale-110 transition-transform">
                    <Layers className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-gray-900 block">Alosafe</span>
                    <span className="text-xs text-gray-500">Leads captured from export channels</span>
                  </div>
                </div>
                <span className="text-lg font-bold text-sky-700 bg-sky-100/80 px-3 py-1 rounded-lg">
                  {formatIndianNumber(sectionStats?.companySourceCounts?.alosafe || 0)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Video Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Video Status</h3>
          </div>
          {isSectionStatsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 animate-pulse">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 h-20">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="h-10 w-10 bg-gray-200 rounded-xl flex-shrink-0"></div>
                    <div className="flex-1 space-y-2 min-w-0">
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-2 bg-gray-200 rounded w-1/3"></div>
                    </div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded w-8 flex-shrink-0"></div>
                </div>
              ))}
            </div>
          ) : videoStatusesList.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No active video statuses configured.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {videoStatusesList.map((status) => {
                const count = sectionStats?.videoStatusCounts?.[status._id] || 0;
                return (
                  <div
                    key={status._id}
                    onClick={() => handleFilterRedirect('leadVideoStatus', status._id)}
                    className="flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100 transition-all rounded-xl border border-purple-100 cursor-pointer group shadow-sm hover:shadow"
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="p-3 bg-purple-500 rounded-xl text-white group-hover:scale-110 transition-transform flex-shrink-0">
                        <Video className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-semibold text-gray-900 block truncate capitalize" title={status.name}>{status.name}</span>
                        <span className="text-xs text-gray-500 truncate block">Verification stage</span>
                      </div>
                    </div>
                    <span className="text-lg font-bold text-purple-700 bg-purple-100/80 px-3 py-1 rounded-lg ml-2 flex-shrink-0">
                      {formatIndianNumber(count)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Order Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Order Status</h3>
          </div>
          {isSectionStatsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 animate-pulse">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 h-20">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="h-10 w-10 bg-gray-200 rounded-xl flex-shrink-0"></div>
                    <div className="flex-1 space-y-2 min-w-0">
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-2 bg-gray-200 rounded w-1/3"></div>
                    </div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded w-8 flex-shrink-0"></div>
                </div>
              ))}
            </div>
          ) : orderStatusesList.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No active order statuses configured.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {orderStatusesList.map((status) => {
                const count = sectionStats?.orderStatusCounts?.[status._id] || 0;
                return (
                  <div
                    key={status._id}
                    onClick={() => handleFilterRedirect('leadOrderStatus', status._id)}
                    className="flex items-center justify-between p-4 bg-teal-50 hover:bg-teal-100 transition-all rounded-xl border border-teal-100 cursor-pointer group shadow-sm hover:shadow"
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="p-3 bg-teal-500 rounded-xl text-white group-hover:scale-110 transition-transform flex-shrink-0">
                        <ShoppingBag className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-semibold text-gray-900 block truncate capitalize" title={status.name}>{status.name}</span>
                        <span className="text-xs text-gray-500 truncate block">Order tracking stage</span>
                      </div>
                    </div>
                    <span className="text-lg font-bold text-teal-700 bg-teal-100/80 px-3 py-1 rounded-lg ml-2 flex-shrink-0">
                      {formatIndianNumber(count)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Call Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Call Status</h3>
          </div>
          {isSectionStatsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 animate-pulse">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 h-20">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="h-10 w-10 bg-gray-200 rounded-xl flex-shrink-0"></div>
                    <div className="flex-1 space-y-2 min-w-0">
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-2 bg-gray-200 rounded w-1/3"></div>
                    </div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded w-8 flex-shrink-0"></div>
                </div>
              ))}
            </div>
          ) : callStatusesList.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No active call statuses configured.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {callStatusesList.map((status) => {
                const count = sectionStats?.callStatusCounts?.[status._id] || 0;
                return (
                  <div
                    key={status._id}
                    onClick={() => handleFilterRedirect('leadCallStatus', status._id)}
                    className="flex items-center justify-between p-4 bg-indigo-50 hover:bg-indigo-100 transition-all rounded-xl border border-indigo-100 cursor-pointer group shadow-sm hover:shadow"
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="p-3 bg-indigo-500 rounded-xl text-white group-hover:scale-110 transition-transform flex-shrink-0">
                        <PhoneCall className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-semibold text-gray-900 block truncate capitalize" title={status.name}>{status.name}</span>
                        <span className="text-xs text-gray-500 truncate block">Telephony tracking</span>
                      </div>
                    </div>
                    <span className="text-lg font-bold text-indigo-700 bg-indigo-100/80 px-3 py-1 rounded-lg ml-2 flex-shrink-0">
                      {formatIndianNumber(count)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Interest Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Interest Status</h3>
          </div>
          {isSectionStatsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 animate-pulse">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 h-20">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="h-10 w-10 bg-gray-200 rounded-xl flex-shrink-0"></div>
                    <div className="flex-1 space-y-2 min-w-0">
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-2 bg-gray-200 rounded w-1/3"></div>
                    </div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded w-8 flex-shrink-0"></div>
                </div>
              ))}
            </div>
          ) : interestStatusesList.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No active interest levels configured.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {interestStatusesList.map((status) => {
                const count = sectionStats?.interestCounts?.[status._id] || 0;
                return (
                  <div
                    key={status._id}
                    onClick={() => handleFilterRedirect('leadInterest', status._id)}
                    className="flex items-center justify-between p-4 bg-rose-50 hover:bg-rose-100 transition-all rounded-xl border border-rose-100 cursor-pointer group shadow-sm hover:shadow"
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="p-3 bg-rose-500 rounded-xl text-white group-hover:scale-110 transition-transform flex-shrink-0">
                        <Heart className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-semibold text-gray-900 block truncate capitalize" title={status.name}>{status.name}</span>
                        <span className="text-xs text-gray-500 truncate block">Prospect interest level</span>
                      </div>
                    </div>
                    <span className="text-lg font-bold text-rose-700 bg-rose-100/80 px-3 py-1 rounded-lg ml-2 flex-shrink-0">
                      {formatIndianNumber(count)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Leads Filters */}
      {(currentUser?.role === 'super_admin' || currentUser?.role === 'sales_manager' || currentUser?.role === 'export_manager') && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Agent By</h3>
          </div>
          {/* First Row: Selectors and Reset Button */}
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            {currentUser?.role === 'super_admin' && (
              <div className="flex-1 w-full sm:w-auto relative manager-dropdown">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manager
                </label>
                <div
                  onClick={() => setManagerSearchOpen(!managerSearchOpen)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 bg-white cursor-pointer flex items-center justify-between h-10 hover:border-gray-400"
                >
                  <span className={`text-sm ${selectedManager ? 'text-gray-900' : 'text-gray-500'} truncate`}>
                    {selectedManager ? (selectedManagerData?.name || 'Selected Manager') : 'All Managers'}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${managerSearchOpen ? 'rotate-180' : ''}`} />
                </div>

                {managerSearchOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden">
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Search Managers..."
                          value={managerSearchTerm}
                          onChange={(e) => setManagerSearchTerm(e.target.value)}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      <div
                        className="px-4 py-2 text-sm hover:bg-blue-50 cursor-pointer text-gray-700"
                        onClick={() => {
                          setSelectedManager('');
                          setSelectedManagerData(null);
                          setSelectedSalesExecutive('');
                          setSelectedExecutiveData(null);
                          setManagerSearchOpen(false);
                          setManagerSearchTerm('');
                        }}
                      >
                        All Managers
                      </div>
                      {managers.map((m) => (
                        <div
                          key={m.id || m._id}
                          className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0"
                          onClick={() => {
                            setSelectedManager(m.id || m._id);
                            setSelectedManagerData(m);
                            setSelectedSalesExecutive('');
                            setSelectedExecutiveData(null);
                            setManagerSearchOpen(false);
                            setManagerSearchTerm('');
                          }}
                        >
                          <div className="text-sm font-medium text-gray-900">{m.name}</div>
                          <div className="text-xs text-gray-500">
                            {m.employeeId ? `${m.employeeId} • ` : ''}{m.email}
                          </div>
                          {m.phone && <div className="text-xs text-gray-400">{m.phone}</div>}
                        </div>
                      ))}
                      {managers.length === 0 && (
                        <div className="px-4 py-4 text-sm text-gray-500 text-center">No managers found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {(currentUser?.role === 'super_admin' || currentUser?.role === 'sales_manager' || currentUser?.role === 'export_manager') && (
              <div className="flex-1 w-full sm:w-auto relative executive-dropdown">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sales Executive
                </label>
                <div
                  onClick={() => {
                    if (currentUser?.role === 'super_admin' && !selectedManager) return;
                    setExecutiveSearchOpen(!executiveSearchOpen);
                  }}
                  className={`mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 bg-white cursor-pointer flex items-center justify-between h-10 ${currentUser?.role === 'super_admin' && !selectedManager ? 'bg-gray-50 cursor-not-allowed' : 'hover:border-gray-400'}`}
                >
                  <span className={`text-sm ${selectedSalesExecutive ? 'text-gray-900' : 'text-gray-500'} truncate`}>
                    {selectedSalesExecutive ? (selectedExecutiveData?.name || 'Selected Executive') : 'All Sales Executives'}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${executiveSearchOpen ? 'rotate-180' : ''}`} />
                </div>

                {executiveSearchOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden">
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Search Sales Executives..."
                          value={executiveSearchTerm}
                          onChange={(e) => setExecutiveSearchTerm(e.target.value)}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      <div
                        className="px-4 py-2 text-sm hover:bg-blue-50 cursor-pointer text-gray-700"
                        onClick={() => {
                          setSelectedSalesExecutive('');
                          setSelectedExecutiveData(null);
                          setExecutiveSearchOpen(false);
                          setExecutiveSearchTerm('');
                        }}
                      >
                        All Sales Executives
                      </div>
                      {salesExecutives.map((e) => (
                        <div
                          key={e.id || e._id}
                          className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0"
                          onClick={() => {
                            setSelectedSalesExecutive(e.user || e.id || e._id);
                            setSelectedExecutiveData(e);
                            setExecutiveSearchOpen(false);
                            setExecutiveSearchTerm('');
                          }}
                        >
                          <div className="text-sm font-medium text-gray-900">{e.name}</div>
                          <div className="text-xs text-gray-500">
                            {e.employeeId ? `${e.employeeId} • ` : ''}{e.email}
                          </div>
                          {e.phone && <div className="text-xs text-gray-400">{e.phone}</div>}
                        </div>
                      ))}
                      {salesExecutives.length === 0 && (
                        <div className="px-4 py-4 text-sm text-gray-500 text-center">No executives found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex-1 w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Range
              </label>
              <DateRangeFilter
                startDate={customDateRange.startDate}
                endDate={customDateRange.endDate}
                onChange={(range) => setCustomDateRange(range)}
                allowFutureDates={false}
                inputClassName="pl-2 pr-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-full cursor-pointer h-10"
              />
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedManager('');
                setSelectedManagerData(null);
                setSelectedSalesExecutive('');
                setSelectedExecutiveData(null);
                setManagerSearchTerm('');
                setExecutiveSearchTerm('');
                setCustomDateRange({ startDate: '', endDate: '' });
                setSelectedPeriod('last_30_days');
              }}
              className="h-10 px-4"
            >
              Reset Filters
            </Button>
          </div>

          {/* Second Row: Detailed Agent Info */}
          {(selectedManager || selectedSalesExecutive) && (
            <div className="flex flex-col sm:flex-row gap-4 mt-4 pt-4 border-t border-gray-100">
              {selectedManager && selectedManagerData && (
                <div className="flex-1 p-2 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="text-[10px] text-blue-600 font-bold uppercase tracking-wider mb-1">Selected Manager</div>
                  <div className="text-sm font-semibold text-gray-900">{selectedManagerData.name}</div>
                  <div className="text-xs text-gray-600 truncate">{selectedManagerData.email}</div>
                </div>
              )}
              {selectedSalesExecutive && selectedExecutiveData && (
                <div className="flex-1 p-2 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="text-[10px] text-blue-600 font-bold uppercase tracking-wider mb-1">Selected Sales Executive</div>
                  <div className="text-sm font-semibold text-gray-900">{selectedExecutiveData.name}</div>
                  <div className="text-xs text-gray-600 truncate">{selectedExecutiveData.email}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Loading State */}
        {leadsLoading && (
          <div className="w-full animate-pulse divide-y divide-gray-200">
            <div className="bg-gray-50 h-12 flex items-center px-4">
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4 ml-auto"></div>
            </div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 flex items-center px-4 space-x-6">
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-150 rounded w-1/2"></div>
                </div>
                <div className="h-6 bg-gray-200 rounded-full w-24"></div>
                <div className="h-4 bg-gray-200 rounded w-20"></div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
              </div>
            ))}
          </div>
        )}

        {/* Desktop Table View - Exactly matching LeadListFilters */}
        {!leadsLoading && (
          <div className="hidden lg:block overflow-x-auto w-full">
            <table className="min-w-[1200px] divide-y divide-gray-200 table-auto w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px] max-w-[300px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <div className="flex items-center gap-2">
                        <span>Lead Details</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {formatIndianNumber(pagination?.totalLeads || filteredLeads.length || 0)}
                        </span>
                      </div>
                      {sortColumn === 'name' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('source')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Source</span>
                      {sortColumn === 'source' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('score')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Score</span>
                      {sortColumn === 'score' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortColumn === 'status' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('owner')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Owner</span>
                      {sortColumn === 'owner' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('value')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Value</span>
                      {sortColumn === 'value' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('updatedAt')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Last Updated</span>
                      {sortColumn === 'updatedAt' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                        <p className="text-lg font-medium">No leads found</p>
                        <p className="text-sm mt-2">
                          {leads.length === 0
                            ? 'No leads have been created yet. Create your first lead to get started.'
                            : 'Try adjusting your filters to see more results.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  getSortedLeads().map((lead) => {
                    const leadId = lead.id || lead._id || lead.leadId;
                    return (
                      <tr key={leadId} className="hover:bg-gray-50">
                        <td className="px-4 py-4 min-w-[200px] max-w-[300px]">
                          <div className="flex items-center space-x-3 min-w-0">
                            <div className="min-w-0 flex-1 overflow-hidden">
                              <div className="text-sm font-medium text-gray-900 truncate capitalize" title={getLeadName(lead)}>{getLeadName(lead)}</div>
                              <div className="text-sm text-gray-500 truncate first-letter:capitalize" title={lead.company || 'No company'}>{lead.company || 'No company'}</div>
                              <div className="text-xs text-gray-400 truncate" title={lead.email || 'No email'}>{lead.email || 'No email'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[100px]">
                          <span className="text-sm text-gray-900 truncate block capitalize" title={lead.source || 'Manual Entry'}>{lead.source || 'Manual Entry'}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 min-w-[80px]">
                          {lead.score}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[100px]">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(mapStageToStatusName(lead.stage) || 'New')}`}>
                            {mapStageToStatusName(lead.stage) || 'New'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[120px]">
                          <div>
                            <span className="text-sm text-gray-900 first-letter:capitalize truncate block" title={getOwnerDisplayText(lead)}>{renderOwnerWithHighlight(lead)}</span>
                            {lead.ownerEmail && (
                              <span className="text-xs text-gray-500 truncate block" title={lead.ownerEmail}>{lead.ownerEmail}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[100px]">
                          <span className="text-sm font-medium text-gray-900">₹{lead.leadValue ? formatIndianAmount(lead.leadValue) : '0'}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[140px]">
                          <span className="text-sm text-gray-500">{formatDate(lead.updatedAt || lead.createdAt)}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium min-w-[100px]">
                          <PermissionGate permission="lead_management" action="read">
                            <button
                              onClick={() => handleViewLead(leadId)}
                              className="text-blue-600 hover:text-blue-900 flex-shrink-0"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile Card View - Exactly matching LeadListFilters */}
        {!leadsLoading && (
          <div className="lg:hidden">
            {filteredLeads.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium text-gray-900 mb-2">No leads found</p>
                <p className="text-sm text-gray-500">
                  {leads.length === 0
                    ? 'No leads have been created yet.'
                    : 'Try adjusting your filters.'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {getSortedLeads().map((lead) => {
                  const leadId = lead.id || lead._id || lead.leadId;
                  return (
                    <div key={leadId} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start space-x-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="text-sm font-medium text-gray-900 truncate capitalize">{getLeadName(lead)}</h4>
                            <span className="text-xs font-medium text-gray-900">
                              {lead.score}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 truncate first-letter:capitalize">{lead.company || 'No company'}</p>
                          <p className="text-xs text-gray-400 truncate">{lead.email || 'No email'}</p>

                          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mt-3">
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500">Source:</span>
                              <span className="text-xs font-medium text-gray-900 truncate capitalize" title={lead.source || 'Manual Entry'}>{lead.source || 'Manual Entry'}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500">Status:</span>
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(mapStageToStatusName(lead.stage) || 'New')}`}>
                                {mapStageToStatusName(lead.stage) || 'New'}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between mt-3">
                            <div className="text-xs text-gray-500">
                              <span className="block">Owner: <span className="first-letter:capitalize">{renderOwnerWithHighlight(lead)}</span></span>
                              <span className="block">Value: ₹{lead.leadValue ? formatIndianAmount(lead.leadValue) : '0'}</span>
                              <span className="block">Created: {formatDate(lead.createdAt)}</span>
                              <span className="block">Last Activity: {formatDate(lead.updatedAt || lead.createdAt)}</span>
                            </div>
                            <PermissionGate permission="lead_management" action="read">
                              <button
                                onClick={() => handleViewLead(leadId)}
                                className="text-blue-600 hover:text-blue-900"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {(() => {
        const leadsTotalItems = Number(pagination?.totalLeads || leads?.length || 0);
        const leadsTotalPages = Number(pagination?.totalPages || 1);
        const effectivePaginationLimit = pagination?.limit || itemsPerPage || 10;
        // Show pagination if there's more than one page
        const shouldShowPagination = !leadsLoading && leadsTotalPages > 1;

        return shouldShowPagination ? (
          <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
              {/* Results Count and Limit Selector */}
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-700">
                  Showing {formatIndianNumber(((pagination.currentPage || 1) - 1) * effectivePaginationLimit + 1)} to {formatIndianNumber(Math.min((pagination.currentPage || 1) * effectivePaginationLimit, leadsTotalItems))} of {formatIndianNumber(leadsTotalItems)} results
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">Items per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage((pagination.currentPage || 1) - 1)}
                  disabled={pagination.hasPrevPage === false || (pagination.currentPage || 1) <= 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {(() => {
                    const totalPages = pagination.totalPages || 1;
                    const currentPageNum = pagination.currentPage || 1;
                    const pages = [];

                    if (totalPages <= 10) {
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      pages.push(1);

                      if (currentPageNum <= 4) {
                        for (let i = 2; i <= 5; i++) {
                          pages.push(i);
                        }
                        pages.push('ellipsis');
                        pages.push(totalPages);
                      } else if (currentPageNum >= totalPages - 3) {
                        pages.push('ellipsis');
                        for (let i = totalPages - 4; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        pages.push('ellipsis');
                        for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                          pages.push(i);
                        }
                        pages.push('ellipsis');
                        pages.push(totalPages);
                      }
                    }

                    return pages.map((page, index) => {
                      if (page === 'ellipsis') {
                        return (
                          <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }

                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                            ? 'bg-agrosaf-green text-white'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          {page}
                        </button>
                      );
                    });
                  })()}
                </div>

                <button
                  onClick={() => setCurrentPage((pagination.currentPage || 1) + 1)}
                  disabled={pagination.hasNextPage === false || (pagination.currentPage || 1) >= (pagination.totalPages || 1)}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        ) : null;
      })()}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
              <button
                onClick={handleCancelDelete}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete this lead? This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={handleCancelDelete}
              >
                Cancel
              </Button>
              <PermissionGate permission="lead_management" action="delete">
                <Button
                  variant="primary"
                  onClick={handleConfirmDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete
                </Button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDashboard;
